import React, { Fragment } from "react";
import Script from "next/script";
import { AnalyticalAccount, ProviderName } from "../../generated/graphql";
import { AnalyticsEnum, AnalyticsIntegartion, StoreIntegration } from "./types";
import { type User } from "../Authentication/hooks/useAuth";

type MailchimpExtractedDataType = {
  apiKey: string;
  server: string;
  listId: string;
  adminEmail: string;
};
export type GameballExtractedDataType = {
  apiKey: string;
  customer: string;
  signupChallenge: string;
  createOrderChallenge: string;
};

export let mailchimpExtractedData: MailchimpExtractedDataType;
export let gameballExtractedData: GameballExtractedDataType;

const HEAD_SNIPPETS = {
  [AnalyticsEnum.Google_Tag_Manager]: (id: string) => (
    <Script
      key="Google_Tag_Manager"
      id="Google_Tag_Manager"
      dangerouslySetInnerHTML={{
        __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${id}');`,
      }}
    />
  ),
  [AnalyticsEnum.Google_Analytics]: (id: string) => (
    <Fragment key="Google_Analytics">
      <Script
        strategy="lazyOnload"
        src={`https://www.googletagmanager.com/gtag/js?id=${id}`}
      />
      <Script
        id="Google_Analytics"
        dangerouslySetInnerHTML={{
          __html: `window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());gtag('config', '${id}');`,
        }}
      />
    </Fragment>
  ),
  [AnalyticsEnum.Facebook_Pixel]: (id: string) => (
    <Script
      id="Facebook_Pixel"
      key="Facebook_Pixel"
      dangerouslySetInnerHTML={{
        __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${id}');
            fbq('track', 'PageView');`,
      }}
    />
  ),
  [AnalyticsEnum.Tiktok_Pixel]: (id: string) => (
    <Script
      id="Tiktok_Pixel"
      key="Tiktok_Pixel"
      // it is beforeInteractive because it will cause a warning in tiktok pixel helper if it is any other strategy
      strategy="beforeInteractive"
      dangerouslySetInnerHTML={{
        __html: `
        !function (w, d, t) {
          w.TiktokAnalyticsObject = t;
          var ttq = w[t] = w[t] || [];
          ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie"];
          ttq.setAndDefer = function (t, e) {
  t[e] = function () {
    t[e] = function () {
      t[e].queue.push([].slice.call(arguments, 0));
    };
    t[e].queue = [];
  };
};
          for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
          ttq.instance = function (t) {
            for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++
            ) ttq.setAndDefer(e, ttq.methods[n]);
            return e;
          };
          ttq.load = function (e, n) {
            var i = "https://analytics.tiktok.com/i18n/pixel/events.js";
            ttq._i = ttq._i || {};
            ttq._i[e] = [];
            ttq._i[e]._u = i;
            ttq._t = ttq._t || {};
            ttq._t[e] = +new Date;
            ttq._o = ttq._o || {};
            ttq._o[e] = n || {};
            var o = document.createElement("script");
            o.type = "text/javascript";
            o.async = !0;
            o.src = i + "?sdkid=" + e + "&lib=" + t;
            o.onload = function() {
              ttq._ready = true;
            };
            var a = document.getElementsByTagName("script")[0];
            a.parentNode.insertBefore(o, a);
          };
          ttq.load('${id}', {
            enableCookie: true,
          });
          ttq.page();
          ttq.isReady = function() {
            return this._ready;
          };
        }(window, document, 'ttq');
      `,
      }}
    />
  ),

  [ProviderName.Klaviyo]: ({ publicKey }) => (
    <Script
      id="klaviyo"
      key="klaviyo"
      strategy="lazyOnload"
      src={`https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${publicKey}`}
    />
  ),

  [ProviderName.Optimonk]: ({ accountId }) => (
    <Script
      id="optimonk"
      key="optimonk"
      strategy="lazyOnload"
      src={`https://onsite.optimonk.com/script.js?account=${accountId}`}
    />
  ),
  [ProviderName.MailChimp]: ({ apiKey, server, listId, adminEmail }) => {
    mailchimpExtractedData = {
      apiKey: apiKey || "",
      server: server || "",
      listId: listId || "",
      adminEmail: adminEmail || "",
    };
  },
  [ProviderName.Gameball]: ({
    apiKey,
    signupChallenge,
    createOrderChallenge,
    locale,
    customer,
  }) => {
    gameballExtractedData = {
      apiKey,
      signupChallenge,
      createOrderChallenge,
      customer,
    };
    const playerUniqueId = customer?.id ? customer?.id?.split("_")[1] : null;

    return (
      <Fragment key="Gameball">
        <Script
          id="Gameball"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `
          window.GbLoadInit = function(){
          GbSdk.init({
            APIKey: "${apiKey}",
            lang: "${locale}",
            playerUniqueId: "${playerUniqueId}",
            playerAttributes: {},
            shop:"wuilt.com",
            platform:"any"
          });
        };`,
          }}
        />
        <Script
          id="gameball"
          key="gameball"
          strategy="lazyOnload"
          src={"https://assets.gameball.co/widget/js/gameball-init.min.js"}
        />
      </Fragment>
    );
  },
};

const BODY_SNIPPETS = {
  [AnalyticsEnum.Google_Tag_Manager]: (id: string) =>
    `<iframe src="https://www.googletagmanager.com/ns.html?id=${id}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
};

export function createScript(
  account: AnalyticsIntegartion | StoreIntegration,
  adminEmail?: string | null,
  customer?: User | undefined,
  locale?: string
) {
  const snippetFn = HEAD_SNIPPETS[account.name];

  if (typeof snippetFn !== "function") {
    return null;
  }

  if (account.type === "store") {
    return snippetFn({
      ...(account as StoreIntegration).scriptControls,
      adminEmail,
      customer,
      locale,
    });
  }

  return snippetFn((account as AnalyticsIntegartion).id.trim());
}

export function createNoScript(account: AnalyticalAccount) {
  if (!BODY_SNIPPETS[account.name]) return null;
  return (
    <noscript
      key={account.name}
      dangerouslySetInnerHTML={{
        __html: BODY_SNIPPETS[account.name](account.id.trim()),
      }}
    />
  );
}

export function getAnalyticsNoScripts(
  accounts: (AnalyticalAccount | null)[] | undefined | null
) {
  return (accounts || [])
    .filter((account) => account?.isActive)
    .map(createNoScript);
}
